import { PreviewRow } from "src/components/DropZoneInputV2/components/PreviewRow/PreviewRow.tsx";
import { PreviewChip } from "src/components/DropZoneInputV2/components/PreviewChip/PreviewChip.tsx";
import * as React from "react";
import type {
  Config,
  MediaObject,
  State,
} from "src/components/DropZoneInputV2/types.ts";
import * as styles from "./Preview.style.ts";
import { DragLayer } from "src/components/DropZoneInputV2/components/DragLayer/DragLayer.tsx";
import { useState } from "react";

export const Preview = ({
  previewType,
  ...restProps
}: {
  onRemove: (object: MediaObject) => void;
  draggedItemId: string | null;
  onChangeDraggedItemId: (draggedItemId: null | string) => void;
  onReplace: (object: MediaObject, file: File) => void;
  previewType: "default" | "chips";
  onMove: (item: MediaObject, moveToIndex: number) => void;
  invalidMedia: State["invalidMedia"];
  media: State["media"];
  maxItems?: number;
  validationRules?: Partial<Config["validationRules"]>;
}) => {
  if (previewType === "default") {
    return <DefaultPreview {...restProps} />;
  }
  return <ChipsPreview {...restProps} />;
};

const DefaultPreview = ({
  onRemove,
  onReplace,
  validationRules,
  media,
  invalidMedia,
  onMove,
  draggedItemId,
  onChangeDraggedItemId,
  maxItems,
}: {
  onRemove: (object: MediaObject) => void;
  onReplace: (object: MediaObject, file: File) => void;
  invalidMedia: State["invalidMedia"];
  media: State["media"];
  draggedItemId: string | null;
  onMove: (object: MediaObject, moveToIndex: number) => void;
  maxItems?: number;
  onChangeDraggedItemId: (draggedItemId: null | string) => void;
  validationRules?: Partial<Config["validationRules"]>;
}) => {
  const [itemWidth, setItemWidth] = useState(0);
  const maxItemsUploaded = !!maxItems && maxItems <= media.length;

  const handleRemoveFile = (object: MediaObject) => () => {
    onRemove(object);
  };

  const handleReplaceFile = (object: MediaObject) => (newFile: File) => {
    onReplace(object, newFile);
  };

  return (
    !!media?.length && (
      <div
        ref={(node) => {
          if (node) {
            // 30 px is container padding
            setItemWidth(node.clientWidth - 30);
          }
        }}
        css={styles.previewList({
          maxItemsUploaded,
        })}
      >
        <DragLayer width={itemWidth} draggedItemId={draggedItemId} />
        {media.map((object, index) => {
          return (
            <PreviewRow
              draggedItemId={draggedItemId}
              onChangeDraggedItemId={onChangeDraggedItemId}
              validationRules={validationRules}
              key={object.attributes.uuid}
              index={index}
              onMove={onMove}
              media={media}
              object={object}
              onRemove={handleRemoveFile(object)}
              onReplace={handleReplaceFile(object)}
            />
          );
        })}
      </div>
    )
  );
};

const ChipsPreview = ({
  onRemove,
  onReplace,
  validationRules,
  invalidMedia,
  media,
  maxItems,
}: {
  onRemove: (object: MediaObject) => void;
  onReplace: (object: MediaObject, file: File) => void;
  invalidMedia: State["invalidMedia"];
  media: State["media"];
  maxItems?: number;
  validationRules?: Partial<Config["validationRules"]>;
}) => {
  const maxItemsUploaded = !!maxItems && maxItems <= media.length;

  const handleRemoveFile = (object: MediaObject) => () => {
    onRemove(object);
  };

  const handleReplaceFile = (object: MediaObject) => (newFile: File) => {
    onReplace(object, newFile);
  };
  return (
    !!media?.length && (
      <div
        css={styles.chipsPreviewList({
          maxItemsUploaded,
        })}
      >
        {media.map((object) => (
          <PreviewChip
            errors={object.clientValidationErrors}
            validationRules={validationRules}
            key={object.attributes.uuid}
            url={
              object.attributes.preview_url ||
              // object.client_preview || it is not working right for .csv files
              undefined
            }
            onRemove={handleRemoveFile(object)}
            onReplace={handleReplaceFile(object)}
            imageName={object.attributes.name}
          />
        ))}
      </div>
    )
  );
};
